import { useCallback, useEffect, useRef, useState } from "react";

/** The page in the sidebar that has focus when the drawer closes after a resize: the current page, else the first link. */
function focusSidebarPage(): void {
  const link =
    document.querySelector<HTMLElement>('#app-sidebar a[aria-current="page"]') ??
    document.querySelector<HTMLElement>("#app-sidebar a");
  link?.focus();
}

/**
 * The navigation drawer of a narrow screen: its open state, the menu button it returns focus to, and the rules that close it.
 *
 * The drawer exists under 768 px (`md`). If the window grows past that while it is open (a tablet turns, a window is dragged), the sidebar
 * is on screen again: the drawer closes, and its focus trap must not outlive its screen. A new page starts with the drawer closed (a link in
 * the drawer also closes it, this covers the browser back button).
 */
export function useDrawer(pathname: string) {
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  // Set when the window grew past the drawer width: the menu button is hidden then, so focus goes to the sidebar instead.
  const closedByResize = useRef(false);
  // The resize rule below acts only on an open drawer: a resize while it is closed must not leave the flag set for its next close.
  const isOpen = useRef(false);
  isOpen.current = open;

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (!query.matches || !isOpen.current) return;
      closedByResize.current = true;
      setOpen(false);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the address is the trigger
  useEffect(() => setOpen(false), [pathname]);

  // The menu button is not a Radix trigger, so focus goes back to it here. After a resize it is hidden: the sidebar takes focus instead.
  const onCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    if (closedByResize.current) {
      closedByResize.current = false;
      focusSidebarPage();
      return;
    }
    menuButton.current?.focus();
  }, []);

  return { open, setOpen, menuButton, onCloseAutoFocus };
}
